'use client';

import type { ValidationRules } from '@commenda/types';
import { useContext } from 'react';
import { isRuleActive } from '../utils/validation';
import { FormContext } from './FormContext';

interface UseFormFieldOptions {
  rules?: ValidationRules;
  disabledFromProps?: boolean;
}

interface UseFormFieldResult<T> {
  value: T | undefined;
  error: string | undefined;
  disabled: boolean;
  isRequired: boolean;
  /** Update form data and run validation for this field in one call. */
  setValue: (next: T | undefined) => void;
}

/**
 * Shared form-field plumbing for wrapper components. Reads form context,
 * derives `disabled` / `isRequired`, and returns a `setValue` that combines
 * `updateField` + `validateField`. Callers own coercion (e.g. empty-string
 * defaulting) and label rendering.
 */
export function useFormField<T = unknown>(
  fieldId: string,
  options?: UseFormFieldOptions
): UseFormFieldResult<T> {
  const {
    formData,
    updateField,
    errors,
    validateField,
    isFieldVisible,
    isFieldDisabled,
  } = useContext(FormContext);

  return {
    value: formData[fieldId] as T | undefined,
    error: errors[fieldId],
    disabled: Boolean(options?.disabledFromProps) || isFieldDisabled(fieldId),
    isRequired: isRuleActive(options?.rules?.required, isFieldVisible),
    setValue: (next) => {
      updateField(fieldId, next);
      validateField(fieldId, next, options?.rules);
    },
  };
}
